import { CONFIG } from "../config";
import type { NoteEvent } from "../types";

// Floating split-point hand assignment. Pure: a function
// of (notes, opts) only — no clock, no randomness. Sets each note's `hand`.

export interface HandSplitOptions {
  /** Flip L↔R after assignment. */
  swap: boolean;
  /** Shift the split point up (+) / down (−) in semitones. */
  biasSemitones: number;
}

export const DEFAULT_HAND_SPLIT: HandSplitOptions = { swap: false, biasSemitones: 0 };

export function assignHands(notes: NoteEvent[], opts: HandSplitOptions): void {
  if (notes.length === 0) return;

  const sorted = [...notes].sort((a, b) => a.onset - b.onset);
  const epsilon = CONFIG.chordWindowMs / 1000;
  const { alpha, maxMoveSemitones, minGapSemitones } = CONFIG.handSplit;
  let split = seedSplit(sorted);

  let i = 0;
  while (i < sorted.length) {
    const first = sorted[i];
    if (!first) break;
    const sliceStart = first.onset;
    const lefts: number[] = [];
    const rights: number[] = [];
    const threshold = split + opts.biasSemitones;

    while (i < sorted.length) {
      const n = sorted[i];
      if (!n || n.onset - sliceStart > epsilon) break;
      if (n.midi >= threshold) {
        n.hand = "right";
        rights.push(n.midi);
      } else {
        n.hand = "left";
        lefts.push(n.midi);
      }
      i++;
    }

    if (lefts.length > 0 && rights.length > 0) {
      // reduce, not Math.max(...spread) — a pathological mega-chord overflows the
      // argument stack (RangeError) past ~125k notes.
      let topLeft = lefts[0] ?? 0;
      for (const m of lefts) if (m > topLeft) topLeft = m;
      let bottomRight = rights[0] ?? 0;
      for (const m of rights) if (m < bottomRight) bottomRight = m;
      if (bottomRight - topLeft >= minGapSemitones) {
        const gapMid = (topLeft + bottomRight) / 2;
        let move = alpha * (gapMid - split);
        if (move > maxMoveSemitones) move = maxMoveSemitones;
        if (move < -maxMoveSemitones) move = -maxMoveSemitones;
        split += move;
      }
    }
  }

  if (opts.swap) {
    for (const n of notes) {
      if (n.hand === "left") n.hand = "right";
      else if (n.hand === "right") n.hand = "left";
    }
  }
}

/** Midpoint of the two track means when the file is cleanly 2-track; else C4. */
function seedSplit(sorted: NoteEvent[]): number {
  const byGroup = new Map<number, { sum: number; count: number }>();
  for (const n of sorted) {
    const g = byGroup.get(n.group) ?? { sum: 0, count: 0 };
    g.sum += n.midi;
    g.count += 1;
    byGroup.set(n.group, g);
  }
  if (byGroup.size === 2) {
    const means = [...byGroup.values()].map((g) => g.sum / g.count).sort((a, b) => a - b);
    return ((means[0] ?? CONFIG.handSplit.seedMidi) + (means[1] ?? CONFIG.handSplit.seedMidi)) / 2;
  }
  return CONFIG.handSplit.seedMidi;
}
